import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import SendIcon from '@material-ui/icons/Send';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { format, subDays } from 'date-fns';
import { useSnackbar } from 'notistack';

import { Main } from '../../../../components/Main';
import api from '../../../../services/api';
import { generateCustomerReceiptPDF } from '../../../report/templates/customerReceiptReport';

interface IReceiptCustomer {
  customer: string;
  orders: number;
  deliveries: number;
  withoutGps: number;
  drivers: string;
}

interface IReceiptDay {
  date: string;
  customers: IReceiptCustomer[];
}

interface IReceiptActivity {
  tag: string;
  description: string;
  endHour: string;
  lat: string;
  lng: string;
  driverName: string;
  responsibleName: string;
  images: string[];
}

interface IReceiptDetail {
  customer: string;
  date: string;
  email: string | null;
  orders: { orderNumber: string; activities: IReceiptActivity[] }[];
}

const useStyles = makeStyles({
  card: {
    padding: '16px 18px',
    borderRadius: 10,
  },
  cardValue: { fontSize: 30, fontWeight: 800 },
  dateHead: {
    padding: '12px 16px',
    fontWeight: 700,
    borderTop: '1px solid #e6e9ee',
    background: '#fafbfc',
  },
  // viewer no formato do e-mail email-finish-route
  mail: { background: '#cfcfcf', padding: '18px 0 26px' },
  mailInner: {
    width: 540,
    maxWidth: '94%',
    margin: '0 auto',
    background: '#f4f4f4',
    fontFamily: 'Ubuntu, Tahoma, Verdana, Segoe, sans-serif',
  },
  mailHello: { padding: '14px 18px 10px', fontSize: 14, background: '#fff' },
  orderHead: {
    background: '#eef3fb',
    padding: '8px 14px',
    fontWeight: 700,
    fontSize: 13,
    color: '#1d4f9e',
  },
  activity: {
    padding: '12px 14px',
    fontSize: 13.5,
    lineHeight: 1.55,
    borderTop: '1px solid #eee',
    background: '#fff',
  },
  imageBtn: {
    background: '#47ae3e',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    borderRadius: 4,
    padding: '5px 18px',
    textDecoration: 'none',
    display: 'inline-block',
    marginRight: 8,
    marginTop: 4,
  },
});

const formatDateBr = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

export const ReceiptsList = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [initialDate, setInitialDate] = useState(
    format(subDays(new Date(), 7), 'yyyy-MM-dd')
  );
  const [finalDate, setFinalDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [customerName, setCustomerName] = useState('');
  const [driverName, setDriverName] = useState('');
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState<IReceiptDay[]>([]);
  const [counters, setCounters] = useState({
    customers: 0,
    deliveries: 0,
    withoutGps: 0,
  });
  const [detail, setDetail] = useState<IReceiptDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/receipts', {
        params: { initialDate, finalDate, customerName, driverName },
      });
      setDays(response.data.days || []);
      setCounters(response.data.counters || { customers: 0, deliveries: 0, withoutGps: 0 });
    } catch {
      enqueueSnackbar('Erro ao carregar os comprovantes', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [initialDate, finalDate, customerName, driverName, enqueueSnackbar]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (date: string, customer: string) => {
    try {
      const response = await api.get('/receipts/detail', {
        params: { date, customerName: customer },
      });
      setDetail(response.data);
    } catch {
      enqueueSnackbar('Erro ao carregar o comprovante', { variant: 'error' });
    }
  };

  // PDF no layout 2.0: logo da empresa (Parâmetros) e fotos EMBUTIDAS —
  // embedImages=1 faz o servidor baixar as fotos e devolver em base64
  const downloadPdf = async (date: string, customer: string) => {
    try {
      const [response, params] = await Promise.all([
        api.get('/receipts/detail', {
          params: { date, customerName: customer, embedImages: '1' },
        }),
        // logo é opcional: falha aqui não impede o PDF
        api.get('/company-params/by/document').catch(() => null),
      ]);
      const logo = params?.data?.reportLogoBase64 || null;
      await generateCustomerReceiptPDF(response.data, logo);
    } catch {
      enqueueSnackbar('Erro ao gerar o PDF', { variant: 'error' });
    }
  };

  const resend = async (date: string, customer: string) => {
    try {
      const response = await api.post('/receipts/resend', null, {
        params: { date, customerName: customer },
      });
      enqueueSnackbar(
        `E-mail reenviado para ${(response.data.emails || []).join(', ')}`,
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar('Erro ao reenviar o e-mail', { variant: 'error' });
    }
  };

  return (
    <Main>
      <Grid container spacing={3}>
        <Grid item xs={6} md={4}>
          <Paper className={classes.card} style={{ background: '#cfe3f5' }}>
            <Typography variant="body2">Clientes atendidos</Typography>
            <Typography className={classes.cardValue}>{counters.customers}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper className={classes.card} style={{ background: '#bcdfbe' }}>
            <Typography variant="body2">Entregas com comprovante</Typography>
            <Typography className={classes.cardValue}>{counters.deliveries}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper className={classes.card} style={{ background: '#f4ddb4' }}>
            <Typography variant="body2">Sem GPS do app</Typography>
            <Typography className={classes.cardValue}>{counters.withoutGps}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <Grid container spacing={2} style={{ padding: 16 }} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Cliente"
                  fullWidth
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Motorista"
                  fullWidth
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="Data início"
                  type="date"
                  fullWidth
                  value={initialDate}
                  onChange={(e) => setInitialDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="Data fim"
                  type="date"
                  fullWidth
                  value={finalDate}
                  onChange={(e) => setFinalDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={load}
                  disabled={loading}
                >
                  {loading ? 'CARREGANDO...' : 'FILTRAR'}
                </Button>
              </Grid>
            </Grid>

            {days.map((day) => (
              <div key={day.date}>
                <Typography className={classes.dateHead}>
                  {formatDateBr(day.date)} — Clientes atendidos: {day.customers.length}
                </Typography>
                <div style={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Pedidos</TableCell>
                        <TableCell>Entregas</TableCell>
                        <TableCell>Motorista(s)</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {day.customers.map((row) => (
                        <TableRow key={`${day.date}-${row.customer}`} hover>
                          <TableCell>
                            <strong>{row.customer}</strong>
                          </TableCell>
                          <TableCell>{row.orders}</TableCell>
                          <TableCell>{row.deliveries}</TableCell>
                          <TableCell>{row.drivers}</TableCell>
                          <TableCell>
                            {row.withoutGps > 0
                              ? `${row.withoutGps} sem GPS`
                              : 'completo'}
                          </TableCell>
                          <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<VisibilityIcon />}
                              onClick={() => openDetail(day.date, row.customer)}
                            >
                              Ver
                            </Button>
                            <Button
                              size="small"
                              startIcon={<GetAppIcon />}
                              onClick={() => downloadPdf(day.date, row.customer)}
                            >
                              PDF
                            </Button>
                            <Button
                              size="small"
                              startIcon={<SendIcon />}
                              onClick={() => resend(day.date, row.customer)}
                            >
                              Reenviar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}

            {!loading && days.length === 0 && (
              <Typography style={{ padding: 24 }} color="textSecondary">
                Nenhum comprovante no período.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
        aria-label="Comprovante do cliente"
      >
        {detail && (
          <div className={classes.mail}>
            <div style={{ textAlign: 'right', padding: '0 16px 10px' }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => downloadPdf(detail.date, detail.customer)}
              >
                Baixar PDF
              </Button>{' '}
              <Button size="small" onClick={() => resend(detail.date, detail.customer)}>
                Reenviar e-mail
              </Button>{' '}
              <Button size="small" onClick={() => setDetail(null)}>
                Fechar
              </Button>
            </div>
            <div className={classes.mailInner}>
              <div className={classes.mailHello}>
                Olá, <b>{detail.customer}</b>. Segue o comprovante das entregas
                realizadas em <b>{formatDateBr(detail.date)}</b>:
              </div>

              {detail.orders.map((order) => (
                <div key={order.orderNumber} style={{ margin: '12px 10px 0' }}>
                  <div className={classes.orderHead}>
                    Pedido {order.orderNumber} · {order.activities.length}{' '}
                    equipamento{order.activities.length === 1 ? '' : 's'}
                  </div>
                  {order.activities.map((activity, index) => (
                    <div key={index} className={classes.activity}>
                      O equipamento{' '}
                      <strong>
                        {activity.tag}
                        {activity.description ? ` — ${activity.description}` : ''}
                      </strong>{' '}
                      foi <strong>entregue</strong> no dia{' '}
                      <strong>{formatDateBr(detail.date)}</strong>
                      {activity.endHour ? (
                        <>
                          , às <strong>{activity.endHour}</strong>
                        </>
                      ) : null}
                      {activity.lat && activity.lng ? (
                        <>
                          {' '}
                          <a
                            href={`https://maps.google.com/maps?q=${activity.lat},${activity.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#482683' }}
                          >
                            em ({activity.lat}, {activity.lng})
                          </a>
                        </>
                      ) : null}{' '}
                      pelo motorista <strong>{activity.driverName || '-'}</strong>.
                      {activity.responsibleName ? (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>
                          Recebido por: <b>{activity.responsibleName}</b>
                        </div>
                      ) : null}
                      <div>
                        {activity.images.map((img, imageIndex) =>
                          String(img).startsWith('http') ? (
                            <a
                              key={imageIndex}
                              className={classes.imageBtn}
                              href={img}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Imagem {imageIndex + 1}
                            </a>
                          ) : (
                            // legado base64: renderiza inline
                            <img
                              key={imageIndex}
                              src={
                                String(img).startsWith('data:')
                                  ? img
                                  : `data:image/jpeg;base64,${img}`
                              }
                              alt={`Imagem ${imageIndex + 1}`}
                              style={{ maxWidth: 140, borderRadius: 4, marginRight: 8, marginTop: 4 }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <Typography
                style={{
                  textAlign: 'center',
                  fontSize: 11,
                  color: '#666',
                  padding: 12,
                }}
              >
                Comprovante gerado pela plataforma 4LOG
              </Typography>
            </div>
          </div>
        )}
      </Dialog>
    </Main>
  );
};
